import { categories } from "@/mocks/gua";

const toneIcon = { hot: "ri-fire-fill", calm: "ri-arrow-right-up-line", fun: "ri-emotion-laugh-line" };
const toneColor = {
  hot: "bg-primary-100 text-primary-700",
  calm: "bg-secondary-100 text-secondary-800",
  fun: "bg-accent-100 text-accent-700",
};

export default function ForumSection() {
  return (
    <section id="forum" className="w-full bg-background-100/60 py-12 md:py-16" aria-label="版块广场">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-2 text-center">
          <span className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-primary-600">
            <i className="ri-apps-2-line" aria-hidden="true" />
            一站式吃瓜
          </span>
          <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            版块广场 · 各取所爱
          </h2>
          <p className="mx-auto max-w-2xl text-sm text-foreground-600 md:text-base">
            影视、社会、圈内、科技……不同赛道的瓜，去对应的板块围观讨论，热门观点都在那里等你。
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((c) => (
            <a
              key={c.id}
              href="#hot"
              className="group flex flex-col rounded-2xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-background-300"
            >
              <div className="flex items-start justify-between">
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-xl text-2xl ${
                    c.color === "primary"
                      ? "bg-primary-100 text-primary-600"
                      : c.color === "accent"
                        ? "bg-accent-100 text-accent-700"
                        : c.color === "secondary"
                          ? "bg-secondary-100 text-secondary-800"
                          : "bg-background-200 text-foreground-700"
                  }`}
                >
                  <i className={c.icon} aria-hidden="true" />
                </span>
                <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-500">
                  {c.posts} 条热议
                </span>
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{c.name}</h3>
              <p className="mt-1 flex-1 text-sm text-foreground-600">{c.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                去围观
                <i
                  className="ri-arrow-right-line transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </span>
            </a>
          ))}
        </div>

        {/* 社区热点专题 */}
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <div className="overflow-hidden rounded-3xl border border-background-200 bg-background-50">
            <div className="relative aspect-[16/9] w-full overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Playful%20group%20of%20people%20chatting%20around%20a%20bright%20watermelon%20table%20in%20a%20warm%20cozy%20community%20courtyard%2C%20coral%20and%20cream%20and%20soft%20green%20tones%2C%20flat%20modern%20illustration%2C%20lively%20and%20friendly%20mood%2C%20high%20detail%20no%20text&width=1280&height=720&seq=forum-comm-01&orientation=landscape"
                alt="瓜友在社区聚集热聊的场景插画"
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-foreground-950/70 px-3 py-1 text-xs font-semibold text-background-50 backdrop-blur-sm">
                <i className="ri-heart-3-line text-accent-300" aria-hidden="true" /> 社区口碑
              </span>
            </div>
            <div className="p-5 md:p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950">大瓜网凭什么让大家留下来？</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                不只是"搬瓜"，更注重"论瓜"。这里不设对立、只讲事实，前排永远有清醒的声音、有趣的视角和真金白银的观点。看瓜也看人，是瓜友给我们最多的评价。
              </p>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {[
                  { v: "98%", l: "讨论氛围认可" },
                  { v: "4.8/5", l: "吃瓜体验评分" },
                  { v: "600+", l: "每周热瓜专题" },
                ].map((s) => (
                  <div key={s.l} className="rounded-xl bg-background-100 px-3 py-3 text-center">
                    <p className="font-heading text-lg font-extrabold text-primary-600">{s.v}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="font-heading text-lg font-extrabold text-foreground-950">
              版块人气话题
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { tone: "hot" as const, title: "烂尾还是神作？近三年高分剧大起底", reads: "128万" },
                { tone: "fun" as const, title: "那些年我们错过的宿舍名场面", reads: "96万" },
                { tone: "calm" as const, title: "流量大洗牌：谁在认真做内容？", reads: "81万" },
                { tone: "fun" as const, title: "打工人摸鱼小妙招征集帖", reads: "64万" },
              ].map((t) => (
                <article
                  key={t.title}
                  className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-background-300"
                >
                  <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${toneColor[t.tone]}`}>
                    <i className={toneIcon[t.tone]} aria-hidden="true" />
                    {t.tone === "hot" ? "热" : t.tone === "fun" ? "趣" : "论"}
                  </span>
                  <p className="mt-3 flex-1 text-sm font-semibold leading-snug text-foreground-800">{t.title}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs text-foreground-400">
                    <i className="ri-eye-line" aria-hidden="true" /> {t.reads} 阅读
                  </span>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}